import React, { useContext, useState } from "react";
import { usePost } from "../API";
import InterfaceContext, { PublicConfigurationContext } from "../Context";
import { languageContent, safeParse } from "../Util";
import { QuestionProps } from "./Props";
import * as v0 from "@aidkitorg/types/lib/survey";
import { Link } from "react-router-dom";
import { Button } from "@aidkitorg/component-library";

export function useCreateAssociatedApplicant(params: {
  uid: string,
  targetField: string,
  onSuccess: (newId: string) => void,
  onError?: (error: string) => void,
}) {
  const [creating, setCreating] = useState(false);
  const createAssociatedApplicant = usePost('/applicant/create_associated_applicant');

  function create() {
    // Using a callback here guarantees that multiple clicks will not cause multiple API calls
    // because React automatically queues the set requests and executes them sequentially.
    setCreating((currentCreating) => {
      if (currentCreating) {
        console.log("Already clicked");
        return currentCreating;
      }
      (async () => {
        try {
          const response = await createAssociatedApplicant({
            existingApplicantUID: params.uid,
            applicantCreatorTargetField: params.targetField,
          });
          if (response && typeof response === 'object' && response.success) {
            params.onSuccess(response.newId);
          } else {
            params.onError?.((response && typeof response === 'object' && (response as any)?.error) || 'Failed to create applicant');
          }
        } catch (error) {
          console.error("Error in createAssociatedApplicant", error);
          params.onError?.('Creation failed.');
        } finally {
          setCreating(false);
        }
      })();
      return true;
    });
  }

  return { creating, create };
}

export function ApplicantCreator(props: QuestionProps) {
  const context = useContext(InterfaceContext);
  const content = props[languageContent(context.lang)];
  const metadata = safeParse(props.Metadata || '{}') as Partial<v0.ApplicantCreator>;

  const { creating, create } = useCreateAssociatedApplicant({
    uid: props.uid || '',
    targetField: props['Target Field'] || '',
    onSuccess: (newId) => {
      if (metadata.openOnCreated?.mode === 'New Tab') {
        window.open(`/a/${newId}`, '_blank');
      } else if (metadata.openOnCreated?.mode === 'Same Tab') {
        window.location.href = `/a/${newId}`;
      }
    },
  });

  return <div className="my-2.5">
    {(props.info[props["Target Field"]!] || creating)
      ? <div className='my-1.5'>{creating ? 
        <span>Creating new applicant...</span>
        : <span>Associated applicant created. <Link to={`/a/${props.info[props['Target Field']!]}`} target="_blank">Link {props.info[props['Target Field']!]}</Link></span>
      }</div>
      : (
        <Button
          onClick={create}
          disabled={creating}
        >
          {content}
        </Button>
      )
    }
  </div>;
}
